import {
	ChannelDataDto,
	ChannelDataResponseDto,
	ChannelDto,
	UserSessionStatusDto,
} from "@api-types/chat";
import { httpClient } from "@utils";
import { getApiRequestContext } from "@utils/api-config";
import { getApplicationRequestHeaders } from "@utils/api-config/getApplicationRequestHeader";
import { ApiRequestContextData } from "@utils/api-config/types";

class ChatApiService {
	static get baseUrl(): string {
		return `${WEBPACK_CHAT_API_ADDRESS}/chat`;
	}

	static getRequestHeaders = (context: ApiRequestContextData) => {
		const appHeaders = getApplicationRequestHeaders(context);

		appHeaders["Content-Type" as keyof HeadersInit] = "application/json";
		appHeaders["Accept" as keyof HeadersInit] = "application/json";
		return appHeaders;
	};

	static async fetchAmityApiInfo(): Promise<{
		apiKey: string;
		apiRegion: string;
	}> {
		const context = getApiRequestContext();

		return httpClient.get(
			`${this.baseUrl}/api-key`,
			this.getRequestHeaders(context)
		);
	}

	static async fetchAuthToken(userId: string): Promise<{
		authenticationToken: string;
		displayName: string;
		userId: string;
		hasClosedSupportChatLink: boolean;
	}> {
		const context = getApiRequestContext();

		//returns 400 status if user doesnt exist or something went wrong
		return httpClient.post(
			`${this.baseUrl}/user-authentication-token/${userId}`,
			null,
			this.getRequestHeaders(context)
		);
	}

	static async setSupportChatLinkStatus(userId: string): Promise<void> {
		const context = getApiRequestContext();

		return httpClient.post(
			`${this.baseUrl}/support-chat-link-close/${userId}`,
			null,
			this.getRequestHeaders(context)
		);
	}

	static async fetchChannelData(
		channelData: ChannelDataDto
	): Promise<ChannelDataResponseDto> {
		const context = getApiRequestContext();

		return httpClient.post(
			`${this.baseUrl}/create-or-get-channel`,
			JSON.stringify(channelData),
			this.getRequestHeaders(context)
		);
	}

	static async fetchUserChannels(
		username: string,
		culture: string,
		accessToken: string
	): Promise<ChannelDataResponseDto> {
		const context = getApiRequestContext();

		return httpClient.post(
			`${this.baseUrl}/user-channels`,
			JSON.stringify({ username, culture, accessToken }),
			this.getRequestHeaders(context)
		);
	}

	static async createSupportChannel(
		username: string,
		accessToken: string
	): Promise<ChannelDataResponseDto> {
		const context = getApiRequestContext();

		return httpClient.post(
			`${this.baseUrl}/create-support-channel`,
			JSON.stringify({ username, accessToken }),
			this.getRequestHeaders(context)
		);
	}

	static async getSupportChannel(
		username: string,
		accessToken: string
	): Promise<ChannelDataResponseDto> {
		const context = getApiRequestContext();

		return httpClient.post(
			`${this.baseUrl}/get-support-channel`,
			JSON.stringify({ username, accessToken }),
			this.getRequestHeaders(context)
		);
	}

	static async revokeUserSession(
		userId: string
	): Promise<UserSessionStatusDto> {
		const context = getApiRequestContext();

		return httpClient.delete(
			`${this.baseUrl}/revoke-user-session/${userId}`,
			this.getRequestHeaders(context),
			{ responseType: "raw" }
		);
	}

	static async queryChannelInfo(
		channelId: string,
		culture: string
	): Promise<ChannelDto> {
		const context = getApiRequestContext();

		return httpClient.get(
			`${this.baseUrl}/channel/${channelId}?culture=${culture}`,
			this.getRequestHeaders(context)
		);
	}
}

export default ChatApiService;
